import { Link, useNavigate } from 'react-router-dom';
import { LogIn, Server, ShieldOff } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAuth } from '@/lib/auth/useAuth';

/**
 * Real Discord OAuth2 (Batch 5) — this page now has three genuine states
 * (loading / unauthenticated / authenticated), not a permanent placeholder.
 * The guild list shown for an authenticated user is the real list from
 * GET /api/auth/me; nothing here is fabricated. Bot-presence per guild
 * (`botPresent`) is real when the Bot Bridge is configured and reachable,
 * and only falls back to "unknown" (`null`) when the Bridge itself can't
 * answer — never guessed either way.
 */
export function GuildSelectorPage() {
  const { status, guilds, login } = useAuth();
  const navigate = useNavigate();

  if (status === 'loading') {
    return (
      <div className="min-h-full flex items-center justify-center p-6">
        <Card className="max-w-md w-full space-y-3">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </Card>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <div className="min-h-full flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <EmptyState
            icon={Server}
            title="Sign in to manage a server"
            description="You'll be taken to Discord to authorize Dem Web, then brought back here."
            action={
              <Button onClick={login} className="w-full">
                <LogIn size={16} aria-hidden="true" />
                Sign in with Discord
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-full p-6">
      <div className="max-w-lg mx-auto">
        <h1 className="text-h1 text-text-primary mb-1">Choose a server</h1>
        <p className="text-small text-text-secondary mb-6">
          Servers you have Discord permissions in. Dashboard sections are further gated per-feature
          by the specific permission each one needs, not just server access.
        </p>

        {guilds.length === 0 ? (
          <Card>
            <EmptyState
              icon={ShieldOff}
              title="No manageable servers found"
              description="Your Discord account isn't a member of any server, or none grant you permissions this dashboard uses yet."
            />
          </Card>
        ) : (
          <div className="space-y-2">
            {guilds.map((guild) => (
              <button
                key={guild.id}
                type="button"
                onClick={() => navigate(`/dashboard/${guild.id}`)}
                className="w-full flex items-center gap-3 p-3 rounded-md bg-bg-surface border border-border-subtle hover:border-border text-left transition-colors"
              >
                {guild.icon ? (
                  <img
                    src={`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png?size=64`}
                    alt=""
                    className="h-10 w-10 rounded-full shrink-0"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-bg-elevated flex items-center justify-center text-text-tertiary shrink-0">
                    <Server size={18} aria-hidden="true" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-body-medium text-text-primary truncate">{guild.name}</p>
                  <p className="text-small text-text-tertiary">
                    {guild.botPresent === null ? 'Bot presence unknown' : guild.botPresent ? 'Bot is in this server' : 'Bot not in this server'}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        <Link to="/dashboard/preview-server" className="block mt-6 text-small text-accent-settings-400 hover:underline">
          Preview the dashboard shell with a placeholder server →
        </Link>
      </div>
    </div>
  );
}
